import { Button } from "@bcc/components/ui/button";
import { cn } from "@bcc/lib/utils";
import type { RowSelectionState, Table } from "@tanstack/react-table";
import { ListChecks } from "lucide-react";
import type { DataTableRow } from "./data-table.types";
import { setSelectAllButtonTestId } from "./data-table.utils";
import { useSelectAll } from "./use-select-all";

interface DataTableSelectAllToggleProps<T extends object> {
  table: Table<DataTableRow<T>>;
  label: string;
  maxSelectableRows?: number;
  setRowSelection?: (selection: RowSelectionState) => void;
}

/**
 * Labelled select-all button for the header.
 *
 * The label is rendered as visible button text so it is discoverable without hovering. It is
 * too wide for the 50px selection column, so the header places it in its own full-width row
 * rather than in the checkbox cell.
 *
 * With nothing selectable it goes `aria-disabled` rather than `disabled`, so it stays
 * focusable for keyboard users.
 */
export function DataTableSelectAllToggle<T extends object>({
  table,
  label,
  maxSelectableRows,
  setRowSelection,
}: Readonly<DataTableSelectAllToggleProps<T>>) {
  const { isAllSelected, hasSelectableRows, toggleSelectAll } = useSelectAll({ table, maxSelectableRows, setRowSelection });

  return (
    <Button
      type="button"
      size="sm"
      variant="secondary"
      testId={setSelectAllButtonTestId()}
      aria-pressed={isAllSelected}
      aria-disabled={!hasSelectableRows}
      className={cn(
        "cursor-pointer border border-border",
        isAllSelected && "border-foreground bg-foreground text-background hover:bg-foreground/90",
        !hasSelectableRows && "cursor-not-allowed opacity-50"
      )}
      onClick={() => hasSelectableRows && toggleSelectAll(!isAllSelected)}
    >
      <ListChecks />
      {label}
    </Button>
  );
}
